;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import type {
  Breadcrumb,
  ErrorEvent,
  Exception,
  StackFrame,
} from '@sentry/core'

const ABORT_ERROR_TYPE = 'AbortError'
const FARO_WEB_SDK_MARKER = '@grafana/faro-web-sdk'
const UNHANDLED_REJECTION_MECHANISM = 'onunhandledrejection'

/**
 * Returns whether a browser Sentry error event should be dropped because it is
 * Faro's own collector-request timeout, reported as an unhandled AbortError.
 *
 * Users never see these; Faro already handles the failed batch. Matching frames
 * only works in unminified builds. Production relies on the latest failed fetch
 * breadcrumb targeting the configured Faro collector URL.
 */
export function shouldDropFaroCollectorAbortErrorSentryEvent(
  event: ErrorEvent,
  faroCollectorUrl: string,
): boolean {
  if (!hasUnhandledAbortError(event)) {
    return false
  }

  return (
    hasFaroWebSdkFrame(event) ||
    isLatestFetchBreadcrumbFailedFaroCollectorRequest(event, faroCollectorUrl)
  )
}

function hasUnhandledAbortError(event: ErrorEvent): boolean {
  return event.exception?.values?.some(isUnhandledAbortError) ?? false
}

function isUnhandledAbortError(exception: Exception): boolean {
  return isAbortError(exception) && isUnhandledRejection(exception)
}

function isAbortError(exception: Exception): boolean {
  return exception.type === ABORT_ERROR_TYPE
}

function isUnhandledRejection(exception: Exception): boolean {
  const mechanismType = exception.mechanism?.type
  return (
    typeof mechanismType === 'string' &&
    mechanismType.includes(UNHANDLED_REJECTION_MECHANISM)
  )
}

function hasFaroWebSdkFrame(event: ErrorEvent): boolean {
  return (
    event.exception?.values?.some((exception) =>
      exception.stacktrace?.frames?.some(isFaroWebSdkFrame),
    ) ?? false
  )
}

function isFaroWebSdkFrame(frame: StackFrame): boolean {
  return [frame.filename, frame.abs_path, frame.module].some((value) =>
    value?.includes(FARO_WEB_SDK_MARKER),
  )
}

function isLatestFetchBreadcrumbFailedFaroCollectorRequest(
  event: ErrorEvent,
  faroCollectorUrl: string,
): boolean {
  const latestFetchBreadcrumb = event.breadcrumbs?.findLast(isFetchBreadcrumb)
  if (!latestFetchBreadcrumb) {
    return false
  }

  if (latestFetchBreadcrumb.data?.status_code != null) {
    return false
  }

  const requestUrl = latestFetchBreadcrumb.data?.url
  return (
    typeof requestUrl === 'string' &&
    isFaroCollectorUrl(requestUrl, faroCollectorUrl)
  )
}

function isFetchBreadcrumb(breadcrumb: Breadcrumb): boolean {
  return breadcrumb.category === 'fetch'
}

function isFaroCollectorUrl(
  requestUrl: string,
  faroCollectorUrl: string,
): boolean {
  if (faroCollectorUrl === '') {
    return false
  }

  return (
    requestUrl === faroCollectorUrl ||
    requestUrl.startsWith(`${faroCollectorUrl}?`) ||
    requestUrl.startsWith(`${faroCollectorUrl}/`)
  )
}
